import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import Loading from './components/Loading';
import User from './pages/User';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import NotFound from './pages/NotFound';
import Setting from './pages/Setting';
import EditUser from './pages/User/EditUser';
import AddUser from './pages/User/AddUser';
import { API, normalizeServerAddress, showError, showNotice } from './helpers';
import PasswordResetForm from './components/PasswordResetForm';
import GitHubOAuth from './components/GitHubOAuth';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';
import Channel from './pages/Channel';
import Token from './pages/Token';
import EditToken from './pages/Token/EditToken';
import EditChannel from './pages/Channel/EditChannel';
import Redemption from './pages/Redemption';
import EditRedemption from './pages/Redemption/EditRedemption';
import TopUp from './pages/TopUp';
import Log from './pages/Log';
import Chat from './pages/Chat';
import LarkOAuth from './components/LarkOAuth';
import Dashboard from './pages/Dashboard';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Docs = lazy(() => import('./pages/Docs'));
const Guide = lazy(() => import('./pages/Guide'));
const Examples = lazy(() => import('./pages/Examples'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Sla = lazy(() => import('./pages/Sla'));
const Aup = lazy(() => import('./pages/Aup'));
const Pricing = lazy(() => import('./pages/Pricing'));
const ModelRates = lazy(() => import('./pages/ModelRates'));
const Plans = lazy(() => import('./pages/Plans'));
const VendorModels = lazy(() => import('./pages/VendorModels'));

function App() {
  const { t } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState, statusDispatch] = useContext(StatusContext);

  const loadUser = () => {
    let user = localStorage.getItem('user');
    if (user) {
      let data = JSON.parse(user);
      userDispatch({ type: 'login', payload: data });
    }
  };
  const loadStatus = async () => {
    try {
      const res = await API.get('/api/status');
      const { success, message, data } = res.data || {}; // Add default empty object
      if (success && data) {
        if (data.server_address) {
          data.server_address = normalizeServerAddress(data.server_address);
        }
        localStorage.setItem('status', JSON.stringify(data));
        statusDispatch({ type: 'set', payload: data });
        localStorage.setItem('system_name', data.system_name);
        localStorage.setItem('logo', data.logo);
        localStorage.setItem('footer_html', data.footer_html);
        localStorage.setItem('quota_per_unit', data.quota_per_unit);
        localStorage.setItem('display_in_currency', data.display_in_currency);
        if (data.chat_link) {
          localStorage.setItem('chat_link', data.chat_link);
        } else {
          localStorage.removeItem('chat_link');
        }
        if (
          data.version !== process.env.REACT_APP_VERSION &&
          data.version !== 'v0.0.0' &&
          process.env.REACT_APP_VERSION !== ''
        ) {
          showNotice(
            t('messages.notice.version_available', { version: data.version })
          );
        }
      } else {
        showError(message || t('messages.error.server_status_failed'));
      }
    } catch (error) {
      showError(error.message || t('messages.error.server_status_failed'));
    }
  };

  useEffect(() => {
    loadUser();
    loadStatus().then();
    document.title = 'REALITYPATCH';
    const linkElement = document.querySelector("link[rel~='icon']");
    if (linkElement) {
      linkElement.href = 'data:,';
    }
  }, []);

  return (
    <Routes>
      <Route
        path='/'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path='/channel'
        element={
          <AdminRoute>
            <Channel />
          </AdminRoute>
        }
      />
      <Route
        path='/channel/edit/:id'
        element={
          <AdminRoute>
            <Suspense fallback={<Loading></Loading>}>
              <EditChannel />
            </Suspense>
          </AdminRoute>
        }
      />
      <Route
        path='/channel/add'
        element={
          <AdminRoute>
            <Suspense fallback={<Loading></Loading>}>
              <EditChannel />
            </Suspense>
          </AdminRoute>
        }
      />
      <Route
        path='/token'
        element={
          <PrivateRoute>
            <Token />
          </PrivateRoute>
        }
      />
      <Route
        path='/token/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/token/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/redemption'
        element={
          <PrivateRoute>
            <Redemption />
          </PrivateRoute>
        }
      />
      <Route
        path='/redemption/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/redemption/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/user'
        element={
          <PrivateRoute>
            <User />
          </PrivateRoute>
        }
      />
      <Route
        path='/user/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/edit'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <AddUser />
          </Suspense>
        }
      />
      <Route
        path='/user/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetConfirm />
          </Suspense>
        }
      />
      <Route
        path='/login'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LoginForm />
          </Suspense>
        }
      />
      <Route
        path='/register'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <RegisterForm />
          </Suspense>
        }
      />
      <Route
        path='/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetForm />
          </Suspense>
        }
      />
      <Route
        path='/oauth/github'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <GitHubOAuth />
          </Suspense>
        }
      />
      <Route
        path='/oauth/lark'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LarkOAuth />
          </Suspense>
        }
      />
      <Route
        path='/setting'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <Setting />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/topup'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <TopUp />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/log'
        element={
          <PrivateRoute>
            <Log />
          </PrivateRoute>
        }
      />
      <Route
        path='/about'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <About />
          </Suspense>
        }
      />
      <Route
        path='/vendors/:slug'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <VendorModels />
          </Suspense>
        }
      />
      <Route
        path='/pricing'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Pricing />
          </Suspense>
        }
      />
      <Route
        path='/plans'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Plans />
          </Suspense>
        }
      />
      <Route
        path='/model-rates'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <ModelRates />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/docs'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Docs />
          </Suspense>
        }
      />
      <Route
        path='/guide'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Guide />
          </Suspense>
        }
      />
      <Route
        path='/examples'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Examples />
          </Suspense>
        }
      />
      <Route
        path='/terms'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Terms />
          </Suspense>
        }
      />
      <Route
        path='/privacy'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Privacy />
          </Suspense>
        }
      />
      <Route
        path='/sla'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Sla />
          </Suspense>
        }
      />
      <Route
        path='/aup'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Aup />
          </Suspense>
        }
      />
      <Route
        path='/chat'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Chat />
          </Suspense>
        }
      />
      <Route
        path='/dashboard'
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

export default App;
